import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Radio, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useGlobals } from 'src/shared/contexts/appContext';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { getPermission } from 'src/shared/functions/getSysProductPermissions';
import { PermissionEnum } from 'src/shared/models/enums/permissionEnums';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { InvoiceTemplateOptions } from 'src/shared/models/options/invoiceTemplateOptions';
import { createInvoiceTemplate, getAllInvoiceTemplate } from 'src/shared/services/invoiceService';
import { invoiceTemplateBlueprints, NewInvoiceTemplateType, findInvoiceTemplateBlueprint } from '../models/invoiceTemplateBlueprints';

interface CreateInvoiceTemplateModalProps {
  open: boolean;
  onClose: () => void;
  ownerId: number;
  // Id:t på den skapade mallen, så att den kan öppnas för redigering direkt.
  onCreated: (templateId: number) => void;
}

/**
 * Skapar en ny fakturamall. Typen väljs först, eftersom den avgör både vilka fält som har effekt
 * och vilka försändelser mallen ska ha. Förlagorna ligger i invoiceTemplateBlueprints och är
 * hämtade ur mallar som redan är i drift.
 */
export const CreateInvoiceTemplateModal = ({ open, onClose, ownerId, onCreated }: CreateInvoiceTemplateModalProps) => {
  const { t } = useTranslation(['invoice', 'common']);
  const isMounted = useIsMounted();

  const [type, setType] = useState<NewInvoiceTemplateType>('member');
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  // Autogiro och swish dras av varsitt autojobb som utgår från en mall. Finns det två i samma
  // fakturagrupp vet jobbet inte vilken som gäller, så typen erbjuds bara när den saknas.
  const [takenTypes, setTakenTypes] = useState<string[]>([]);

  const { currentUser } = useGlobals();
  const canCreateInternalTypes = getPermission(currentUser, PermissionEnum.InvoiceCreateForCustomer);

  useEffect(() => {
    if (!open || !ownerId) return;

    const fetchExisting = async () => {
      try {
        const options = new InvoiceTemplateOptions({ owner_id: ownerId, pageSize: 100, pageIndex: 0, types: 'autogiro,swish' });
        const result = await getAllInvoiceTemplate(options);
        if (!isMounted()) return;
        setTakenTypes((result.data.results ?? []).map((template) => template.type ?? '').filter((value) => !!value));
      } catch (err) {
        ConsoleHelper.log(err);
      }
    };

    fetchExisting();
  }, [open, ownerId, isMounted]);

  const available = invoiceTemplateBlueprints.filter(
    (blueprint) => !takenTypes.includes(blueprint.type) && (!blueprint.superAdminOnly || canCreateInternalTypes),
  );

  const close = () => {
    setType('member');
    setName('');
    onClose();
  };

  const create = async () => {
    if (!ownerId || !name.trim()) return;

    setSaving(true);
    try {
      const blueprint = findInvoiceTemplateBlueprint(type);
      const result = await createInvoiceTemplate(blueprint.build(ownerId, name));
      const templateId = typeof result.data === 'number' ? result.data : 0;

      if (!isMounted()) return;

      toast.success(t('invoice:adminInvoiceTemplatePage.createTemplate.created') as string);
      close();

      if (templateId > 0) onCreated(templateId);
    } catch (err) {
      ConsoleHelper.log(err);
      if (isMounted()) toast.error(t('invoice:adminInvoiceTemplatePage.createTemplate.failed') as string);
    } finally {
      if (isMounted()) setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{t('invoice:adminInvoiceTemplatePage.createTemplate.title')}</DialogTitle>
      <DialogContent>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          {t('invoice:adminInvoiceTemplatePage.createTemplate.info')}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
          {available.map((blueprint) => {
            const selected = blueprint.type === type;

            return (
              <Box
                key={blueprint.type}
                onClick={() => setType(blueprint.type)}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  p: 1.5,
                  borderRadius: 1,
                  cursor: 'pointer',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: selected ? 'primary.main' : 'divider',
                  bgcolor: selected ? 'action.hover' : 'transparent',
                }}
              >
                <Radio checked={selected} size='small' sx={{ p: 0, mt: 0.25 }} />
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>{t(blueprint.labelKey)}</Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {t(blueprint.descriptionKey)}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>

        <TextField
          autoFocus
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && name.trim() && !saving) create();
          }}
          label={t('invoice:adminInvoiceTemplatePage.createTemplate.nameLabel')}
          helperText={t('invoice:adminInvoiceTemplatePage.createTemplate.nameHelper')}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant='text' onClick={close} disabled={saving}>
          {t('common:button.cancel')}
        </Button>
        <Button
          variant='contained'
          disableElevation
          onClick={create}
          disabled={saving || !name.trim() || !ownerId}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          {t('common:button.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
